<script setup lang="ts">
import { ref } from 'vue'
import TypeaheadPicker from '@/components/TypeaheadPicker.vue'
import { pathwaysUrls } from '@/util/links'
import RemovableList from './RemovableList.vue'
import { searchStandards as searchStandardSet, type StandardMatch } from '@/standards/api'
import { usePickedUuids } from './use_picked_uuids'

const props = withDefaults(defineProps<{
  id: string
  setCode?: string
  selected?: string[]
  standardsMap?: Record<string, StandardMatch>
}>(), {
  setCode: 'CCSS',
  selected: () => [],
  standardsMap: () => ({})
})

const emit = defineEmits<{ 'update:selected': [refs: string[]] }>()

// Tailwind's preflight strips the paragraph spacing and list markers the authored standard
// text depends on. Its lists sit inside this list's rows, so Vue 2 drew them as nested circles.
const STANDARD_TEXT_TYPOGRAPHY = '[&_p]:mb-2.5 [&_:is(ul,ol)]:pl-10 [&_ul]:list-[circle] [&_ol]:list-decimal'

const isExpanded = ref(false)
const pickerId = `${props.id}-${props.setCode}-standards`
const panelId = `${props.id}-standards-panel`

const searchStandards = (query: string, signal: AbortSignal) => searchStandardSet(props.setCode, query, signal)

// No add event: the page refetches the whole set's standards on every change.
const { add, remove } = usePickedUuids(() => props.selected, refs => emit('update:selected', refs), () => {}, 'standard')
</script>

<template>
  <div class="mb-2 border border-gray-400 rounded-xs">
    <button
      type="button"
      class="w-full px-4 py-3 text-left bg-gray-100 hover:underline focus-visible:outline-primary-400"
      :aria-expanded="isExpanded"
      :aria-controls="panelId"
      @click="isExpanded = !isExpanded"
    >
      {{ setCode }} Standards ({{ selected.length }})
    </button>
    <div v-if="isExpanded" :id="panelId" class="p-4">
      <label :for="pickerId" class="sr-only">Add {{ setCode }} standard</label>
      <TypeaheadPicker
        :id="pickerId"
        :search="searchStandards"
        :item-label="standard => standard.standard_long_ref"
        :item-key="standard => standard.standard_long_ref"
        :placeholder="`${setCode}.`"
        empty-text="No standards found"
        @select="(standard: StandardMatch) => add(standard.standard_long_ref, standard.standard_long_ref)"
      />
      <RemovableList
        :items="selected"
        :item-key="standardRef => standardRef"
        :item-label="standardRef => standardRef"
        noun="standard"
        :empty-focus-id="pickerId"
        reorderable
        @remove="remove"
        @reorder="refs => emit('update:selected', refs)"
      >
        <template #item="{ item: standardRef }">
          <p>
            <a
              :href="pathwaysUrls.editStandard(setCode, standardRef)"
              target="_blank"
              :title="`Edit standard ${standardRef}`"
              class="text-primary-400 underline"
            >{{ standardRef }}</a>
          </p>
          <template v-if="standardsMap[standardRef]">
            <!-- eslint-disable-next-line vue/no-v-html -- standard text is authored HTML, rendered as the standard popover does -->
            <p class="mt-1" :class="STANDARD_TEXT_TYPOGRAPHY" v-html="standardsMap[standardRef].standard_text" />
            <div v-if="standardsMap[standardRef].core_purpose" class="mt-2 p-2 rounded-xs bg-blue-100 text-gray-900">
              <b>Core Purpose:</b> {{ standardsMap[standardRef].core_purpose }}
            </div>
          </template>
        </template>
      </RemovableList>
    </div>
  </div>
</template>
